/**
 * Action Center — the single source of truth for "what does Forge need from a
 * human right now, and which buttons resolve it".
 *
 * Two pure halves, shared by the web UI and the backend:
 *   1. `actionCenterFor(state, …)`  — epic state (+ the latest unanswered Forge
 *      prompt) → headline, explanation and the exact buttons to show.
 *   2. `classifyComment` / `buildTimeline` — turns the raw GitHub issue thread
 *      into typed timeline items so the portal can mirror it.
 *
 * Every button posts the SAME slash command a person would type on GitHub, so
 * the orchestrator needs no changes: it already parses first-line commands.
 */

// ---------------------------------------------------------------------------
// Commands
// ---------------------------------------------------------------------------

export type ActionTone = 'primary' | 'secondary' | 'danger';

export interface ActionInput {
  label: string;
  placeholder: string;
  required: boolean;
  /** 'arg' = appended to the command line (/epic-targets a,b); 'note' = on the lines below. */
  mode: 'arg' | 'note';
}

export interface ActionDef {
  id: string;
  label: string;
  /** First line posted to GitHub. Empty string = plain reply (no command). */
  command: string;
  tone: ActionTone;
  help: string;
  input?: ActionInput;
  /** When set the UI asks for confirmation first. */
  confirm?: string;
}

const A = (d: ActionDef): ActionDef => d;

export const ACTIONS = {
  approve: A({
    id: 'approve', label: 'Approve and build', command: '/proceed', tone: 'primary',
    help: 'Approves the plan so Forge starts building.',
    input: { label: 'Note for the agents (optional)', placeholder: 'Anything agents should know before they start…', required: false, mode: 'note' },
  }),
  buildAnyway: A({
    id: 'buildAnyway', label: 'Build anyway', command: '/proceed --force', tone: 'primary',
    help: 'Overrides the budget / quota gate. Spend can exceed the cap.',
    confirm: 'This overrides the budget gate and spend may exceed your cap. Continue?',
  }),
  requestChanges: A({
    id: 'requestChanges', label: 'Request changes', command: '', tone: 'secondary',
    help: 'Posts your feedback as a comment on the issue; Forge re-plans from it.',
    input: { label: 'What should change?', placeholder: 'e.g. Keep the reporting project out of the first slice', required: true, mode: 'note' },
  }),
  reply: A({
    id: 'reply', label: 'Send answers', command: '', tone: 'primary',
    help: 'Posts your answers as a reply on the issue.',
    input: { label: 'Your answers', placeholder: '1. …\n2. …', required: true, mode: 'note' },
  }),
  skip: A({
    id: 'skip', label: 'Skip', command: '/skip', tone: 'secondary',
    help: 'Skips the open question(s) and lets Forge continue with its best assumption.',
  }),
  targets: A({
    id: 'targets', label: 'Confirm targets', command: '/epic-targets', tone: 'primary',
    help: 'Confirms which repositories this epic touches.',
    input: { label: 'Repositories', placeholder: 'org/repo-a, org/repo-b', required: true, mode: 'arg' },
  }),
  approveUnderstanding: A({
    id: 'approveUnderstanding', label: 'Approve understanding', command: '/approve', tone: 'primary',
    help: 'Confirms Forge understood the legacy behaviour correctly.',
  }),
  retry: A({
    id: 'retry', label: 'Retry', command: '/retry', tone: 'primary',
    help: 'Re-runs the failed step with the same context.',
    input: { label: 'Guidance for the retry (optional)', placeholder: 'e.g. The pool timeout is 30s in CI', required: false, mode: 'note' },
  }),
  restart: A({
    id: 'restart', label: 'Restart', command: '/restart', tone: 'secondary',
    help: 'Starts the failed unit over from scratch.',
    confirm: 'Restart from scratch? Work already done on it is discarded.',
  }),
  unblock: A({
    id: 'unblock', label: 'Unblock', command: '/unblock', tone: 'secondary',
    help: 'Tells Forge the blocker is resolved so it can resume.',
  }),
  waive: A({
    id: 'waive', label: 'Waive with reason', command: '/parity waive', tone: 'secondary',
    help: 'Accepts the parity gap. The reason is recorded on the issue.',
    input: { label: 'Reason', placeholder: 'Why is this gap acceptable?', required: true, mode: 'arg' },
    confirm: 'Waiving records a permanent exception for this epic. Continue?',
  }),
  intervene: A({
    id: 'intervene', label: 'Post decision', command: '/intervene', tone: 'primary',
    help: 'Leaves a steering note the agents must follow.',
    input: { label: 'Your decision', placeholder: 'Which side is right, and what should happen?', required: true, mode: 'note' },
  }),
  resume: A({
    id: 'resume', label: 'Resume', command: '/resume', tone: 'primary', help: 'Resumes a paused epic.',
  }),
  pause: A({
    id: 'pause', label: 'Pause', command: '/pause', tone: 'secondary', help: 'Stops Forge starting new work.',
  }),
  requote: A({
    id: 'requote', label: 'Re-quote', command: '/requote', tone: 'secondary', help: 'Recomputes the cost quote.',
  }),
  abort: A({
    id: 'abort', label: 'Abort epic', command: '/abort', tone: 'danger',
    help: 'Stops this epic permanently.',
    confirm: 'Abort this epic? This cannot be undone.',
  }),
} as const;

export type ActionKey = keyof typeof ACTIONS;

/** Operator controls offered in the composer's "/" menu, whatever the state. */
export const OPERATOR_ACTIONS: ActionKey[] = ['pause', 'resume', 'requote', 'intervene'];

/** Commands the post endpoint accepts (first token, plus the `--force` / `waive` variants). */
export const ALLOWED_COMMANDS: string[] = Array.from(
  new Set(Object.values(ACTIONS).map((a) => a.command).filter(Boolean)),
);

/** Build the exact comment body for an action + the user's input. */
export function buildActionBody(def: ActionDef, input?: string): string {
  const text = (input ?? '').trim();
  if (!def.command) return text;
  if (def.input?.mode === 'arg') return text ? `${def.command} ${text}` : def.command;
  return text ? `${def.command}\n\n${text}` : def.command;
}

// ---------------------------------------------------------------------------
// State → required human action
// ---------------------------------------------------------------------------

export type Severity = 'attention' | 'problem' | 'info';

export interface ActionCenterModel {
  severity: Severity;
  headline: string;
  detail: string;
  /** Buttons, first one is the primary call to action. */
  actions: ActionKey[];
  /** Show a link to the PR instead of / alongside buttons. */
  linkPr?: boolean;
  /** What the checklist should remind the person to look at. */
  checklist?: string[];
}

const QUESTION_STATES = new Set([
  'REQUIREMENTS_QUESTIONS', 'PLAN_QUESTIONS', 'PARITY_SCOPE_QUESTIONS', 'INIT_QUESTIONS',
]);
const FAILED_STATES = new Set(['FAILED', 'INIT_FAILED', 'PARITY_FAILED', 'PARITY_RUN_FAILED', 'ROLLED_BACK']);

export interface PendingPrompt {
  title: string;
  htmlUrl: string;
  createdAt: string;
  excerpt?: string;
}

/** Epic states that need nothing more from anyone. */
export const TERMINAL_STATES = new Set([
  'COMPLETE', 'COMPLETED', 'DONE', 'MERGED', 'BUGFIX_COMPLETE', 'INIT_COMPLETE', 'ABORTED', 'ABANDONED',
]);

export interface ActionCenterOpts {
  /** Latest Forge failure no person has acted on yet (merge failed, deploy failed…). */
  blocker?: PendingPrompt | null;
  /** Milliseconds since the epic last changed. */
  idleMs?: number;
  /** An agent is genuinely running — never call such an epic idle. */
  hasLiveAgent?: boolean;
}

export const IDLE_THRESHOLD_MS = 24 * 3600_000;

export function formatIdle(ms: number): string {
  const h = Math.floor(ms / 3600_000);
  return h < 48 ? `${h} hours` : `${Math.floor(h / 24)} days`;
}

/**
 * What the person needs to do for an epic in `state`, or null when Forge is
 * working and nothing is required (the "Operator controls" menu still applies).
 * `pending` is the latest unanswered Forge prompt from the thread — it refines
 * the generic state (e.g. a budget gate inside an otherwise normal state).
 */
export function actionCenterFor(
  state: string,
  pending?: PendingPrompt | null,
  opts: ActionCenterOpts = {},
): ActionCenterModel | null {
  const model = baseActionCenter(state, pending);
  if (model) return model;
  if (TERMINAL_STATES.has(state)) return null;

  // Nothing is asked of the person, but Forge stopped on a failure.
  const b = opts.blocker;
  if (b) {
    const t = b.title.toLowerCase();
    if (/merge/.test(t)) {
      return {
        severity: 'problem',
        headline: 'Merge to develop failed',
        detail: 'Forge could not merge the epic branch. Resolve the conflicts in the repos listed below (or rebase the epic branch), then retry. The epic stays here until you do.',
        actions: ['retry', 'abort'],
        checklist: ['Open each repo listed below and resolve the merge conflict', 'Press Retry so Forge merges again'],
      };
    }
    if (/deploy|rollback/.test(t)) {
      return {
        severity: 'problem',
        headline: b.title,
        detail: 'A deploy step failed. Check the message below, fix the cause, then retry.',
        actions: ['retry', 'abort'],
      };
    }
    return {
      severity: 'problem',
      headline: b.title,
      detail: 'Forge stopped on a failure. Read the message below, then retry or restart.',
      actions: ['retry', 'restart', 'abort'],
    };
  }

  // Nothing asked, nothing failed, nothing running, and no movement for a day.
  if (!opts.hasLiveAgent && (opts.idleMs ?? 0) > IDLE_THRESHOLD_MS) {
    return {
      severity: 'info',
      headline: `No activity for ${formatIdle(opts.idleMs as number)}`,
      detail: 'No agent is running and Forge is not asking you for anything. If that is unexpected, check the Activity tab, then retry or abort.',
      actions: ['retry', 'abort'],
    };
  }
  return null;
}

function baseActionCenter(state: string, pending?: PendingPrompt | null): ActionCenterModel | null {
  const p = (pending?.title ?? '').toLowerCase();

  // Budget / quota gates & disputes are signalled by the comment, not the state.
  if (/budget exhausted|dispute/.test(p) && /paused for a human|budget exhausted/.test(p)) {
    return {
      severity: 'attention',
      headline: 'Forge paused: a dispute needs your decision',
      detail: 'The agents could not agree within their budget. Post a decision and Forge resumes.',
      actions: ['intervene', 'resume'],
    };
  }
  if (/budget|quota/.test(p) && /pause|cap|hit|over/.test(p)) {
    return {
      severity: 'attention',
      headline: 'Build paused at a cost gate',
      detail: 'Forge stopped before exceeding your budget or included quota.',
      actions: ['buildAnyway', 'requote'],
    };
  }

  if (QUESTION_STATES.has(state)) {
    return {
      severity: 'attention',
      headline: 'Forge has questions for you',
      detail: 'Answer here and Forge continues. Skipping makes it proceed on its best assumptions.',
      actions: ['reply', 'skip'],
    };
  }
  switch (state) {
    case 'PLAN_POSTED':
      return {
        severity: 'attention',
        headline: 'Approve the plan to start the build',
        detail: 'Nothing is built until you approve. Review the plan, then approve or ask for changes.',
        actions: ['approve', 'requestChanges', 'abort'],
        checklist: ['Scope and slicing look right', 'Cost projection is acceptable', 'Risky items are called out'],
      };
    case 'TARGETS_PENDING':
      return {
        severity: 'attention',
        headline: 'Confirm which repositories this epic touches',
        detail: 'Forge needs the target repos before it can plan.',
        actions: ['targets'],
      };
    case 'PARITY_UNDERSTANDING_POSTED':
      return {
        severity: 'attention',
        headline: 'Confirm Forge understood the legacy behaviour',
        detail: 'Parity testing is built from this understanding, so correct anything that is wrong.',
        actions: ['approveUnderstanding', 'requestChanges'],
      };
    case 'READY_FOR_REVIEW':
    case 'READY_FOR_REVIEW_NOTIFIED':
      return {
        severity: 'attention',
        headline: 'Ready for your review',
        detail: 'All stories are done and deployed. Review and merge the pull request.',
        actions: [],
        linkPr: true,
      };
    case 'PARITY_GATE_BLOCKED':
      return {
        severity: 'problem',
        headline: 'Parity gate is blocking the epic',
        detail: 'Fix the gaps and re-run, or waive them with a recorded reason.',
        actions: ['retry', 'waive'],
      };
    case 'ABORTED':
    case 'ABANDONED':
      return null;
    default:
      if (FAILED_STATES.has(state)) {
        return {
          severity: 'problem',
          headline: 'This epic stopped with an error',
          detail: 'Retry re-runs the failed step. Restart begins again from scratch.',
          actions: ['retry', 'restart', 'abort'],
        };
      }
      return null;
  }
}

// ---------------------------------------------------------------------------
// Comment classification
// ---------------------------------------------------------------------------

export type TimelineKind =
  | 'needs_you'  // Forge is waiting on a person
  | 'problem'    // something failed / blocked
  | 'success'    // a milestone passed
  | 'progress'   // routine event, collapsed by default
  | 'info'       // other Forge output
  | 'human'      // a person's comment
  | 'command';   // a slash command a person issued

export interface RawComment {
  id: number;
  author: string;
  createdAt: string;
  htmlUrl: string;
  body: string;
}

export interface TimelineItem {
  id: number;
  kind: TimelineKind;
  fromForge: boolean;
  author: string;
  createdAt: string;
  htmlUrl: string;
  title: string;
  /** Body without the heading line, trimmed to ~600 chars. */
  excerpt: string;
  /** Full body. */
  body: string;
}

const EMOJI = /[\p{Extended_Pictographic}️‍]+/gu;

const HAS_EMOJI = /\p{Extended_Pictographic}/u;

function headingOf(body: string): { raw: string; title: string } | null {
  const first = body.split('\n').find((l) => l.trim() !== '');
  if (!first) return null;
  const m = /^\s*#{1,4}\s+(.*)$/.exec(first);
  if (!m) return null;
  const raw = m[1];
  return { raw, title: raw.replace(EMOJI, '').replace(/\s+/g, ' ').trim() };
}

const NEEDS_YOU = /needs your input|human input needed|paused for a human|budget exhausted|build paused|compute budget cap|epic quota — build paused|waiting for|contract reopen/i;
const PROBLEM = /merge .*failed|merge conflict|blocked|failed|aborted|error handling|rollback failed|validation failed|cannot |unavailable|not found|missing/i;
const SUCCESS = /complete|passed|resolved|ready for review|all green|written|merged|conversion complete|rollback complete|skipped/i;
const PROGRESS = /deploying|auto-retry|retrying|rolling back|diagnosing|auto-resolving|resumed|paused by|queued|retry-analyst|intervention recorded|cost|pinned|probes|projected|budget — \d+%|dispute \(/i;

/** Classify one GitHub comment. Pure. */
export function classifyComment(c: RawComment): TimelineItem {
  const body = c.body ?? '';
  const trimmed = body.trim();
  const base = {
    id: c.id, author: c.author, createdAt: c.createdAt, htmlUrl: c.htmlUrl, body,
  };
  const excerptOf = (skipHeading: boolean) => {
    const lines = trimmed.split('\n');
    const rest = (skipHeading ? lines.slice(1) : lines).join('\n').trim();
    return rest.length > 600 ? `${rest.slice(0, 600).trimEnd()}…` : rest;
  };

  if (/^\/[a-z][a-z-]*/i.test(trimmed)) {
    const first = trimmed.split('\n')[0];
    return { ...base, kind: 'command', fromForge: false, title: first, excerpt: excerptOf(true) };
  }

  const isBot = /\[bot\]$/i.test(c.author) || /^forge/i.test(c.author);
  const h = headingOf(body);
  if (!h && !isBot) {
    return {
      ...base, kind: 'human', fromForge: false,
      title: trimmed.split('\n')[0].slice(0, 120), excerpt: excerptOf(false),
    };
  }

  // Bot comments without a markdown heading ("⚠️ **Merge to develop failed**…",
  // "Plan: …") take their title from the first line, markdown stripped.
  const rawTitle = h
    ? h.title
    : trimmed.split('\n')[0].replace(EMOJI, '').replace(/[*_`#]/g, '').replace(/\s+/g, ' ').trim().slice(0, 140);
  const fromForge = isBot || HAS_EMOJI.test(h?.raw ?? '') || /forge/i.test(h?.raw ?? '');

  let kind: TimelineKind = 'info';
  if (/^plan:/i.test(rawTitle)) kind = 'info';
  else if (NEEDS_YOU.test(rawTitle)) kind = 'needs_you';
  else if (PROBLEM.test(rawTitle)) kind = 'problem';
  else if (PROGRESS.test(rawTitle)) kind = 'progress';
  else if (SUCCESS.test(rawTitle)) kind = 'success';

  if (!fromForge) kind = 'human';
  return { ...base, kind, fromForge, title: rawTitle, excerpt: excerptOf(true) };
}

export interface Timeline {
  items: TimelineItem[];
  /** Latest Forge prompt that no human command/reply has followed, else null. */
  pending: TimelineItem | null;
  /** Latest Forge failure that no person has acted on since, else null. */
  blocker: TimelineItem | null;
}

/** Oldest first. `pending`/`blocker` are the last needs_you / problem not followed by a person. */
export function buildTimeline(comments: RawComment[]): Timeline {
  const items = comments
    .map(classifyComment)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.id - b.id);
  let pending: TimelineItem | null = null;
  let blocker: TimelineItem | null = null;
  for (const it of items) {
    if (it.kind === 'needs_you') pending = it;
    else if (it.kind === 'problem' && it.fromForge) blocker = it;
    else if (it.kind === 'command' || (it.kind === 'human' && !it.fromForge)) {
      pending = null;
      blocker = null;
    }
  }
  return { items, pending, blocker };
}
